import React from 'react';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { AddButton, Checkbox, ChipGroup, RemoveButton } from '../../wizard/kit/controls';
import { SelectField, TextField } from '../../wizard/kit/fields';
import { FieldRow, Hint, Line, Sub, Words } from '../../wizard/kit/layout';
import { LIMIT_PERIODS, LimitPeriod, WEEK_DAYS } from '../../wizard/wizardModel';
import { FieldWidth } from '../../wizard/wizardStyles';
import { DayTimeWindow, KeyValueRow } from '../configModel';
import { TC } from './ConfigPanel';

const title = (key: string) => key.charAt(0).toUpperCase() + key.slice(1);

interface IDayTimeFieldsProps {
  label: string;
  value: DayTimeWindow;
  onChange: (value: DayTimeWindow) => void;
  /** "UTC+08:00", or "UTC+8:00 (tenant timezone)" on the rule schedule (54). */
  zoneLabel: string;
  /** The reward draws "Start time / End time" (62), the rule "From / To" (54). */
  rangeLabels: 'fromTo' | 'startEnd';
}

/** A checked line, then the day chips and the time window it unlocks (54, 62). */
export const DayTimeFields: React.FC<IDayTimeFieldsProps> = ({
  label,
  value,
  onChange,
  zoneLabel,
  rangeLabels
}) => {
  const { t } = useMultiLanguageTranslation();
  const [fromLabel, toLabel] =
    rangeLabels === 'fromTo'
      ? [t(`${TC}.from`), t(`${TC}.to`)]
      : [t(`${TC}.startTime`), t(`${TC}.endTime`)];
  return (
    <>
      <Line>
        <Checkbox
          checked={value.on}
          label={label}
          showLabel
          onChange={on => onChange({ ...value, on })}
        />
      </Line>
      <Sub>
        <FieldRow style={{ opacity: value.on ? 1 : 0.45 }}>
          <Words $muted>{t(`${TC}.days`)}</Words>
          <ChipGroup
            label={t(`${TC}.days`)}
            value={value.days}
            disabled={!value.on}
            options={WEEK_DAYS.map(day => ({ value: day, label: title(day) }))}
            onChange={days => onChange({ ...value, days })}
          />
        </FieldRow>
        <FieldRow style={{ opacity: value.on ? 1 : 0.45 }}>
          <Words $muted>{t(`${TC}.timeWindow`)}</Words>
          <Line style={{ padding: 0 }}>
            <Words $muted>{fromLabel}</Words>
            <TextField
              label={fromLabel}
              placeholder={t(`${TC}.timePlaceholder`)}
              value={value.from}
              disabled={!value.on}
              onChange={from => onChange({ ...value, from })}
            />
            <Words $muted>{toLabel}</Words>
            <TextField
              label={toLabel}
              placeholder={t(`${TC}.timePlaceholder`)}
              value={value.to}
              disabled={!value.on}
              onChange={to => onChange({ ...value, to })}
            />
            <Hint>{zoneLabel}</Hint>
          </Line>
        </FieldRow>
      </Sub>
    </>
  );
};

interface ILimitLineProps {
  label: string;
  on: boolean;
  value: string;
  onToggle: (on: boolean) => void;
  onValue: (value: string) => void;
  unit?: string;
  period?: LimitPeriod;
  onPeriod?: (period: LimitPeriod) => void;
  /** 64 capitalises its period menu ("Day"), 54 does not ("day"). */
  periodCase?: 'lower' | 'title';
  /** 64 sets the lead-in left and the controls against the panel's right edge; 54 runs inline. */
  spread?: boolean;
}

const Half = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;

  > * + * {
    margin-left: 11px;
  }
`;

/** "[ ] Lead-in [n] unit [period ▾]": an unchecked line means no limit (54, 64). */
export const LimitLine: React.FC<ILimitLineProps> = ({
  label,
  on,
  value,
  onToggle,
  onValue,
  unit,
  period,
  onPeriod,
  periodCase = 'lower',
  spread = false
}) => {
  const { t } = useMultiLanguageTranslation();
  const lead = (
    <>
      <Checkbox checked={on} label={label} onChange={onToggle} />
      <Words $muted={!on}>{label}</Words>
    </>
  );
  const controls = (
    <>
      <TextField
        label={label}
        placeholder="0"
        inputMode="numeric"
        value={value}
        disabled={!on}
        onChange={onValue}
      />
      {unit !== undefined ? <Words $muted>{unit}</Words> : null}
      {period !== undefined && onPeriod !== undefined ? (
        <SelectField<LimitPeriod>
          label={`${label} ${unit ?? ''}`.trim()}
          value={period}
          disabled={!on}
          options={LIMIT_PERIODS.map(option => ({
            value: option,
            label: t(`${TC}.${periodCase === 'title' ? 'periodTitle' : 'period'}.${option}`)
          }))}
          onChange={onPeriod}
        />
      ) : null}
    </>
  );
  return spread ? (
    <Line style={{ justifyContent: 'space-between' }}>
      <Half>{lead}</Half>
      <Half>{controls}</Half>
    </Line>
  ) : (
    <Line>
      {lead}
      {controls}
    </Line>
  );
};

const Rows = styled.div`
  display: flex;
  flex-direction: column;
`;

interface IKeyValueRowsProps {
  rows: KeyValueRow[];
  onChange: (rows: KeyValueRow[]) => void;
  keyLabel: string;
  valueLabel: string;
  addLabel: string;
  width?: FieldWidth;
  disabled?: boolean;
}

/** Key / value rows with a dashed add (52 customer details, 53 custom payload). */
export const KeyValueRows: React.FC<IKeyValueRowsProps> = ({
  rows,
  onChange,
  keyLabel,
  valueLabel,
  addLabel,
  width = 'lg',
  disabled = false
}) => {
  const { t } = useMultiLanguageTranslation();
  const set = (index: number, patch: Partial<KeyValueRow>) =>
    onChange(rows.map((row, at) => (at === index ? { ...row, ...patch } : row)));
  return (
    <Rows>
      {rows.map((row, index) => (
        <Line key={index}>
          <TextField
            label={`${keyLabel} ${index + 1}`}
            placeholder={keyLabel}
            width={width}
            value={row.key}
            disabled={disabled}
            onChange={key => set(index, { key })}
          />
          <TextField
            label={`${valueLabel} ${index + 1}`}
            placeholder={valueLabel}
            width={width}
            value={row.value}
            disabled={disabled}
            onChange={value => set(index, { value })}
          />
          {rows.length > 1 ? (
            <RemoveButton
              label={`${t(`${TC}.remove`)} ${index + 1}`}
              onClick={() => onChange(rows.filter((_, at) => at !== index))}
            />
          ) : null}
        </Line>
      ))}
      <Line>
        <AddButton
          label={addLabel}
          disabled={disabled}
          onClick={() => onChange([...rows, { key: '', value: '' }])}
        />
      </Line>
    </Rows>
  );
};
